import React, { Fragment, useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import { getUserDesignFeedApi } from "../api/DesignActions";

const PosterDesigns: React.FC = () => {
  const [posters, setPosters] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchPosters = async (pageNumber = 1, append = false) => {
    if (!user?.user_id) return;

    try {
      if (append) {
        setLoadMoreLoading(true);
      } else {
        setLoading(true);
      }

      const result = await getUserDesignFeedApi(
        user.user_id,
        pageNumber,
        limit
      );

      if (
        result?.success === false ||
        result?.status === false
      ) {
        throw new Error(
          result?.message || "Failed to fetch posters"
        );
      }

      const newData = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.data?.rows)
        ? result.data.rows
        : [];

      setPosters((prev) => {
        if (!append) return newData;

        const map = new Map();

        [...prev, ...newData].forEach((item: any) => {
          map.set(item.design_id || item.id || item.image_url, item);
        });

        return Array.from(map.values());
      });

      setHasMore(newData.length === limit);
      setPage(pageNumber);
    } catch (error) {
      console.error("Poster fetch error:", error);

      if (!append) {
        setPosters([]);
      }

      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadMoreLoading(false);
    }
  };

  useEffect(() => {
    fetchPosters(1, false);
  }, [user?.user_id]);

  const handleLoadMore = () => {
    if (loading || loadMoreLoading || !hasMore) return;
    fetchPosters(page + 1, true);
  };

  const openLightbox = (index: number) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const slides = useMemo(() => {
    return posters
      .map((poster) => ({
        src: poster.image_url || poster.api_image_url,
      }))
      .filter((item) => item.src);
  }, [posters]);

  return (
    <Fragment>
      <Header />
      <Leftnav />

      <div className="main-content right-chat-active poster-main-page">
        <div className="middle-sidebar-bottom poster-middle-bottom">
          <div className="middle-sidebar-left poster-middle-left">
            <div className="poster-page-wrapper">
              <div className="poster-header-card">
                <div>
                  <span className="poster-page-badge">
                    <i className="feather-image" />
                    Posters
                  </span>

                  <h2>Poster Designs</h2>

                  <p>View your uploaded and generated poster designs.</p>
                </div>

                <div className="poster-count-box">
                  <i className="feather-layers" />
                  <div>
                    <strong>{posters.length}</strong>
                    <span>Loaded</span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="poster-grid">
                  {Array.from({ length: limit }).map((_, i) => (
                    <div className="poster-skeleton" key={i}>
                      <div className="poster-skeleton-image" />
                      <div className="poster-skeleton-line poster-skeleton-line--wide" />
                      <div className="poster-skeleton-line poster-skeleton-line--narrow" />
                    </div>
                  ))}
                </div>
              ) : posters.length === 0 ? (
                <div className="poster-empty-card">
                  <div className="poster-empty-icon">
                    <i className="feather-image" />
                  </div>

                  <h4>No posters found</h4>
                  <p>Your posters will appear here once you upload or generate one.</p>
                </div>
              ) : (
                <>
                  <div className="poster-grid">
                    {posters.map((poster, index) => {
                      const imageUrl = poster.image_url || poster.api_image_url;

                      if (!imageUrl) return null;

                      return (
                        <div
                          className="poster-card"
                          key={poster.design_id || poster.id || index}
                          onClick={() => openLightbox(index)}
                        >
                          <div className="poster-image-wrap">
                            <img
                              src={imageUrl}
                              alt={poster.design_title || `Poster ${index + 1}`}
                              loading="lazy"
                              onError={(e: any) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />

                            <div className="poster-image-overlay">
                              <span className="poster-view-btn">
                                <i className="feather-eye" />
                                View
                              </span>
                            </div>
                          </div>

                          <div className="poster-card-body">
                            <h4>{poster.design_title || "Poster Design"}</h4>

                            <span>
                              <i className="feather-calendar" />
                              {poster.uploaded_on
                                ? new Date(poster.uploaded_on).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "Recently uploaded"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasMore && (
                    <div className="poster-load-more-wrap">
                      <button
                        type="button"
                        className="poster-load-more-btn"
                        onClick={handleLoadMore}
                        disabled={loadMoreLoading}
                      >
                        {loadMoreLoading && <span className="poster-btn-spinner" />}
                        {loadMoreLoading ? "Loading" : "Load more"}
                      </button>
                    </div>
                  )}

                  {!hasMore && posters.length > 0 && (
                    <p className="poster-end-text">
                      <i className="feather-check-circle" />
                      No more posters
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          index={photoIndex}
          slides={slides}
        />
      )}

      <Popupchat />
      <Appfooter />

      <style>{`
        /* Palette: navy #0A2354 (primary), blue #2F6FE4 (accent),
           light blue-white #F4F7FC (surface), border #DCE4F5 */

        .poster-main-page {
          background: #F4F7FC;
          min-height: 100vh;
          padding-top: 84px;
          padding-left: 300px;
          padding-right: 18px;
          overflow-x: hidden;
        }

        .poster-middle-bottom,
        .poster-middle-left {
          width: 100%;
          max-width: 100%;
          padding: 0;
          margin: 0;
        }

        .poster-page-wrapper {
          width: 100%;
          padding: 0 0 34px;
        }

        .poster-header-card {
          background: linear-gradient(135deg, #EAF1FF, #FFFFFF);
          border: 1px solid #DCE4F5;
          border-radius: 18px;
          padding: 20px 24px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          box-shadow: 0 8px 24px rgba(10, 35, 84, 0.05);
        }

        .poster-page-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(47, 111, 228, 0.12);
          color: #1447B0;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 5px 11px;
          border-radius: 999px;
          margin-bottom: 8px;
        }

        .poster-header-card h2 {
          margin: 0;
          color: #0A2354;
          font-size: 23px;
          font-weight: 700;
          letter-spacing: -0.3px;
        }

        .poster-header-card p {
          margin: 4px 0 0;
          color: #5B6B8C;
          font-size: 13px;
          font-weight: 400;
        }

        .poster-count-box {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 108px;
          background: #ffffff;
          border: 1px solid #DCE4F5;
          border-radius: 14px;
          padding: 10px 16px;
        }

        .poster-count-box i {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: rgba(47, 111, 228, 0.10);
          color: #2F6FE4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }

        .poster-count-box strong {
          display: block;
          color: #0A2354;
          font-size: 18px;
          line-height: 1.1;
          font-weight: 700;
        }

        .poster-count-box span {
          display: block;
          margin-top: 2px;
          color: #8A93A3;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .poster-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 16px;
        }

        .poster-card {
          background: #ffffff;
          border: 1px solid #DCE4F5;
          border-radius: 18px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 6px 18px rgba(10, 35, 84, 0.045);
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }

        .poster-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 32px rgba(10, 35, 84, 0.12);
          border-color: #B9CCF5;
        }

        .poster-image-wrap {
          position: relative;
          width: 100%;
          height: 210px;
          background: #EAF1FF;
          overflow: hidden;
        }

        .poster-image-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.35s ease;
        }

        .poster-card:hover .poster-image-wrap img {
          transform: scale(1.06);
        }

        .poster-image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(6, 23, 51, 0.55), rgba(6, 23, 51, 0));
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;
          padding: 12px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .poster-card:hover .poster-image-overlay {
          opacity: 1;
        }

        .poster-view-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.95);
          color: #0A2354;
          font-size: 12px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 999px;
        }

        .poster-card-body {
          padding: 12px 14px 14px;
        }

        .poster-card-body h4 {
          margin: 0;
          color: #0A2354;
          font-size: 13.5px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .poster-card-body span {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 6px;
          color: #8A93A3;
          font-size: 11.5px;
          font-weight: 500;
        }

        .poster-card-body span i {
          font-size: 12px;
        }

        /* Skeleton loading state */
        .poster-skeleton {
          background: #ffffff;
          border: 1px solid #DCE4F5;
          border-radius: 18px;
          overflow: hidden;
          padding-bottom: 14px;
        }

        .poster-skeleton-image {
          width: 100%;
          height: 210px;
          background: linear-gradient(90deg, #EAF1FF 25%, #F4F7FC 37%, #EAF1FF 63%);
          background-size: 400% 100%;
          animation: poster-shimmer 1.4s ease infinite;
        }

        .poster-skeleton-line {
          height: 10px;
          border-radius: 6px;
          margin: 12px 14px 0;
          background: linear-gradient(90deg, #EAF1FF 25%, #F4F7FC 37%, #EAF1FF 63%);
          background-size: 400% 100%;
          animation: poster-shimmer 1.4s ease infinite;
        }

        .poster-skeleton-line--wide {
          width: 70%;
        }

        .poster-skeleton-line--narrow {
          width: 40%;
          margin-top: 8px;
        }

        @keyframes poster-shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }

        .poster-empty-card {
          min-height: 360px;
          background: #ffffff;
          border: 1px solid #DCE4F5;
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 30px;
        }

        .poster-empty-icon {
          width: 64px;
          height: 64px;
          border-radius: 18px;
          background: rgba(47, 111, 228, 0.10);
          color: #2F6FE4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          margin-bottom: 14px;
        }

        .poster-empty-card h4 {
          margin: 0;
          color: #0A2354;
          font-size: 18px;
          font-weight: 700;
        }

        .poster-empty-card p {
          margin: 6px 0 0;
          color: #8A93A3;
          font-size: 13px;
          font-weight: 400;
          max-width: 320px;
        }

        .poster-load-more-wrap {
          display: flex;
          justify-content: center;
          margin-top: 26px;
        }

        .poster-load-more-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 150px;
          height: 42px;
          border-radius: 999px;
          border: 1px solid #2F6FE4;
          background: #2F6FE4;
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
          justify-content: center;
          box-shadow: 0 6px 16px rgba(47, 111, 228, 0.28);
          transition: 0.18s ease;
        }

        .poster-load-more-btn:hover:not(:disabled) {
          background: #1447B0;
          border-color: #1447B0;
        }

        .poster-load-more-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        .poster-btn-spinner {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top-color: #ffffff;
          animation: poster-spin 0.7s linear infinite;
        }

        @keyframes poster-spin {
          to { transform: rotate(360deg); }
        }

        .poster-end-text {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin: 24px 0 0;
          text-align: center;
          color: #8A93A3;
          font-size: 13px;
          font-weight: 600;
        }

        .poster-end-text i {
          color: #2F6FE4;
        }

        @media (max-width: 1400px) {
          .poster-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }

        @media (max-width: 1200px) {
          .poster-main-page {
            padding-left: 18px;
            padding-right: 18px;
            padding-top: 78px;
          }
        }

        @media (max-width: 1100px) {
          .poster-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 768px) {
          .poster-main-page {
            padding-left: 12px;
            padding-right: 12px;
          }

          .poster-header-card {
            flex-direction: column;
            align-items: stretch;
            border-radius: 16px;
            padding: 16px;
          }

          .poster-count-box {
            width: 100%;
            justify-content: center;
          }

          .poster-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          .poster-image-wrap,
          .poster-skeleton-image {
            height: 180px;
          }
        }

        @media (max-width: 480px) {
          .poster-main-page {
            padding-top: 16px;
          }

          .poster-grid {
            grid-template-columns: 1fr;
          }

          .poster-image-wrap,
          .poster-skeleton-image {
            height: 260px;
          }

          .poster-header-card h2 {
            font-size: 20px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default PosterDesigns;
